import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Typography from '@mui/material/Typography';
import React from 'react';
import AsyncSelect from 'react-select/async';

import Icon from '@/components/icon';
import Button from '@/components/overrides/button';

import {
  countProductManagerFilters,
  type ProductAvailabilityFilter,
  type ProductBarcodeFilter,
  type ProductManagerFilters,
} from './product-manager-filters';

interface ProductManagerFilterDrawerProps {
  open: boolean;
  filters: ProductManagerFilters;
  defaultShops: Array<{ label: string; value: number | string }>;
  loadShops: (query: string, callback: (options: any[]) => void) => void;
  onChange: (filters: ProductManagerFilters) => void;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
}

const PRODUCT_TYPES = [
  ['all', 'All product types'],
  ['Single', 'Singles'],
  ['Box', 'Boxes'],
  ['Pack', 'Packs / bundles'],
  ['Sampler', 'Samplers'],
  ['Jar', 'Jars'],
  ['Tin', 'Tins'],
  ['Subscription', 'Subscriptions'],
  ['Accessory', 'Accessories'],
];

const CLEANUP_FILTERS: Array<{
  key:
    | 'missingLinkedCigars'
    | 'missingImages'
    | 'missingPrice'
    | 'lowPrice'
    | 'quantityMismatch';
  label: string;
  description: string;
}> = [
  {
    key: 'missingLinkedCigars',
    label: 'Missing linked cigars',
    description: 'Cigar products without confirmed contents',
  },
  {
    key: 'missingImages',
    label: 'Missing images',
    description: 'Products that need visual assets',
  },
  {
    key: 'missingPrice',
    label: 'Missing or invalid price',
    description: 'Products with a zero sale price',
  },
  {
    key: 'lowPrice',
    label: 'No profit margin',
    description: 'Sale price equals cost price',
  },
  {
    key: 'quantityMismatch',
    label: 'Quantity / stock mismatch',
    description: 'Availability and inventory disagree',
  },
];

const FilterSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section style={{ padding: '18px 20px', borderTop: '1px solid #e9ecef' }}>
    <Typography variant="subtitle2" fontWeight={700}>
      {title}
    </Typography>
    {description && (
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    )}
    <div style={{ marginTop: 10 }}>{children}</div>
  </section>
);

const ProductManagerFilterDrawer = ({
  open,
  filters,
  defaultShops,
  loadShops,
  onChange,
  onClose,
  onApply,
  onReset,
}: ProductManagerFilterDrawerProps) => {
  const activeFilterCount = countProductManagerFilters(filters);

  const setAvailability = (availability: ProductAvailabilityFilter) => {
    onChange({
      ...filters,
      availability,
      ...(availability === 'no-issues'
        ? {
            missingLinkedCigars: false,
            missingImages: false,
            missingPrice: false,
            lowPrice: false,
            quantityMismatch: false,
          }
        : {}),
    });
  };

  const setCleanupFilter = (
    key: (typeof CLEANUP_FILTERS)[number]['key'],
    checked: boolean
  ) => {
    onChange({
      ...filters,
      [key]: checked,
      availability:
        checked && filters.availability === 'no-issues'
          ? 'all'
          : filters.availability,
    });
  };

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        style: {
          width: 'min(420px, 100vw)',
          backgroundColor: '#f8f9fa',
        },
      }}
    >
      <div
        style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}
      >
        <div
          style={{
            alignItems: 'center',
            backgroundColor: '#fff',
            display: 'flex',
            padding: '16px 20px',
          }}
        >
          <div style={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Filter products
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {activeFilterCount
                ? `${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'}`
                : 'Showing all products'}
            </Typography>
          </div>
          <button
            type="button"
            className="btn btn-light btn-sm"
            aria-label="Close filters"
            onClick={onClose}
          >
            <Icon name="x" />
          </button>
        </div>

        <div style={{ flex: 1 }}>
          <FilterSection title="Shop">
            <AsyncSelect
              value={filters.selectedShop || { label: 'All Shops', value: -1 }}
              onChange={(selectedShop) =>
                onChange({
                  ...filters,
                  selectedShop:
                    selectedShop && Number(selectedShop.value) >= 0
                      ? selectedShop
                      : null,
                })
              }
              loadOptions={loadShops}
              defaultOptions={[
                { label: 'All Shops', value: -1 },
                ...defaultShops,
              ]}
              isClearable
              placeholder="All Shops"
            />
          </FilterSection>

          <FilterSection title="Product type">
            <select
              className="form-select"
              value={filters.productType}
              onChange={(event) =>
                onChange({ ...filters, productType: event.target.value })
              }
            >
              {PRODUCT_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </FilterSection>

          <FilterSection title="Availability">
            <RadioGroup
              row
              value={filters.availability}
              onChange={(event) =>
                setAvailability(event.target.value as ProductAvailabilityFilter)
              }
            >
              <FormControlLabel value="all" control={<Radio />} label="Any" />
              <FormControlLabel
                value="in-stock"
                control={<Radio />}
                label="In stock"
              />
              <FormControlLabel
                value="out-of-stock"
                control={<Radio />}
                label="Out of stock"
              />
              <FormControlLabel
                value="no-issues"
                control={<Radio />}
                label="Available + priced"
              />
            </RadioGroup>
          </FilterSection>

          <FilterSection
            title="Barcode"
            description="Find products with catalog identifiers or records that still need one."
          >
            <RadioGroup
              row
              value={filters.barcodeStatus}
              onChange={(event) =>
                onChange({
                  ...filters,
                  barcodeStatus: event.target.value as ProductBarcodeFilter,
                })
              }
            >
              <FormControlLabel value="all" control={<Radio />} label="Any" />
              <FormControlLabel
                value="has-barcode"
                control={<Radio />}
                label="Has barcode"
              />
              <FormControlLabel
                value="missing-barcode"
                control={<Radio />}
                label="Missing barcode"
              />
            </RadioGroup>
          </FilterSection>

          <FilterSection
            title="Data cleanup"
            description="Combine these filters to focus the table on records needing attention."
          >
            {CLEANUP_FILTERS.map(({ key, label, description }) => (
              <FormControlLabel
                key={key}
                style={{ alignItems: 'flex-start', display: 'flex', margin: 0 }}
                control={
                  <Checkbox
                    checked={filters[key]}
                    onChange={(event) =>
                      setCleanupFilter(key, event.target.checked)
                    }
                    style={{ paddingLeft: 0 }}
                  />
                }
                label={
                  <span style={{ display: 'block', paddingTop: 7 }}>
                    <span style={{ display: 'block', fontWeight: 500 }}>
                      {label}
                    </span>
                    <small style={{ color: '#6c757d' }}>{description}</small>
                  </span>
                }
              />
            ))}
          </FilterSection>
        </div>

        <div
          style={{
            backgroundColor: '#fff',
            borderTop: '1px solid #dee2e6',
            bottom: 0,
            display: 'flex',
            gap: 10,
            padding: '14px 20px',
            position: 'sticky',
          }}
        >
          <Button onClick={onReset} style={{ flex: 1, textAlign: 'center' }}>
            Reset
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={onApply}
            style={{ flex: 1, textAlign: 'center' }}
          >
            Apply filters
          </Button>
        </div>
      </div>
    </SwipeableDrawer>
  );
};

export default ProductManagerFilterDrawer;
